/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import axios from 'axios';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';

import { selectors as filterBarSelectors } from '../filterBar';
import { RootState } from './types';
import { Team } from './api';
import { filterParamSelector } from './selectors';
import * as actions from './actions';
import { getGranularityForTimeRange } from './utils';

type GetTeamsListArguments = { limit: number; offset: number; };

export const fetchAllTeams = () => async (dispatch: ReduxDispatcher) => {
  let teamsList: Team[] = [];

  const fetchTeams = async (
    { limit, offset }: GetTeamsListArguments = { limit: 100, offset: 0 },
  ): Promise<Team[]> => {
    try {
      const response = await dispatch(actions.getTeamsList({ limit, offset }));

      if (axios.isAxiosError(response)) {
        throw new Error(response.message);
      }

      teamsList = teamsList.concat(response.data.results);

      if (response.data.links.next) {
        const nextUrl = new URL(response.data.links.next);
        const nextParams = new URLSearchParams(nextUrl.search).entries();
        const newArguments = Object.fromEntries(
          Array.from(nextParams).map(([key, value]) => ([key, parseInt(value, 10)])),
        ) as GetTeamsListArguments;

        // don't run into an "endless" loop
        // if we really need to fetch more than 1000 teams, find a better solution
        if (newArguments.offset < 1000) {
          return fetchTeams(newArguments);
        }
      }

      return teamsList;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      dispatch(notificationActions.notifyDanger(
        'Failed to fetch teams. Please refresh the page.', null, false, undefined, true,
      ));

      return teamsList;
    }
  };

  return fetchTeams();
};

export const getOrgActivity = () => (dispatch: ReduxDispatcher, getState: () => RootState) => {
  const params = filterParamSelector(getState());
  const selectedDate = filterBarSelectors.selectDateFilterSelection(getState());
  const {
    start = new Date().getTime(),
    end = new Date().getTime(),
    resourceType,
    granularity,
  } = params;
  const granularities = getGranularityForTimeRange(selectedDate);

  return dispatch(actions.getOrgActivity({
    startDate: start,
    endDate: end,
    granularity: granularities.allowed.includes(granularity) ?
      granularity :
      granularities.recommended,
    orgId: getState().user.organization.id,
    resourceType: (resourceType as string[]).find((item) => item) as string,
  }));
};

export const getTeamActivity = () => (dispatch: ReduxDispatcher, getState: () => RootState) => {
  const params = filterParamSelector(getState());
  const {
    start = new Date().getTime(),
    end = new Date().getTime(),
    team_id: teamId,
    resourceType,
    granularity,
  } = params;
  return dispatch(actions.getTeamActivity({
    startDate: start,
    endDate: end,
    granularity,
    orgId: getState().user.organization.id,
    teamIds: [teamId as string],
    resourceType: (resourceType as string[]).find((item) => item) as string,
  }));
};
